/**
 * src/lib/venue.ts
 * 2004va — Venue-mode data layer.
 *
 * Every Supabase query used by venue-mode UI goes through this module.
 * Keeps the RLS assumption surface in one place and makes tests/mocking
 * straightforward.
 *
 * The picker session identity is stored in sessionStorage keyed by venue slug.
 * When a picker enters a code at /venue/enter we create a row in
 * venue_pickers and remember that row's id for the rest of the session.
 * Closing the tab ends the session (by design — pickers are ephemeral).
 */

import { supabase } from './supabase'
import type {
  Venue,
  VenueAccessCode,
  VenuePicker,
  VenuePick,
  ApprovalRequest,
  ApprovalPolicy,
  PickerAction,
  ApproverRelationship,
  ApprovalChannel,
  MediaItem,
} from '@/types'

// ─── Session storage ──────────────────────────────────────────────────────────

const PICKER_SESSION_KEY_PREFIX = 'room:venue:picker:'

function pickerSessionKey(venueSlug: string): string {
  return `${PICKER_SESSION_KEY_PREFIX}${venueSlug}`
}

export interface PickerSession {
  picker_id:    string
  venue_id:     string
  venue_slug:   string
  display_name: string
  session_type: 'shared_code' | 'named_invite' | 'admin'
  started_at:   string
}

export function getPickerSession(venueSlug: string): PickerSession | null {
  try {
    // Read from localStorage (primary). Fall back to sessionStorage for
    // sessions created before the storage migration landed.
    const raw = localStorage.getItem(pickerSessionKey(venueSlug))
                ?? sessionStorage.getItem(pickerSessionKey(venueSlug))
    if (!raw) return null
    return JSON.parse(raw) as PickerSession
  } catch {
    return null
  }
}

export function setPickerSession(session: PickerSession): void {
  // Store in localStorage so the session survives:
  //  - tab close and reopen
  //  - mobile back-button round-trips
  //  - iOS app switcher / share-sheet returns
  //  - PWA launch from home screen
  // Sessions are scoped to venue slug, so multiple venues can coexist.
  // A session is invalidated by:
  //  - explicit sign-out (clearPickerSession)
  //  - server-side picker row expiry (handled in venue_pickers schema)
  //  - access code revocation (validation fails on next touch)
  localStorage.setItem(pickerSessionKey(session.venue_slug), JSON.stringify(session))
  // Clean up any stale sessionStorage row from before the migration.
  sessionStorage.removeItem(pickerSessionKey(session.venue_slug))
}

export function clearPickerSession(venueSlug: string): void {
  localStorage.removeItem(pickerSessionKey(venueSlug))
  sessionStorage.removeItem(pickerSessionKey(venueSlug))
}

// ─── Venues ───────────────────────────────────────────────────────────────────

export async function fetchVenueBySlug(slug: string): Promise<Venue | null> {
  const { data, error } = await supabase
    .from('venues')
    .select('*')
    .eq('slug', slug)
    .maybeSingle()
  if (error) throw new Error(`fetchVenueBySlug(${slug}): ${error.message}`)
  return data
}

export async function fetchVenueById(id: string): Promise<Venue | null> {
  const { data, error } = await supabase
    .from('venues')
    .select('*')
    .eq('id', id)
    .maybeSingle()
  if (error) throw new Error(`fetchVenueById(${id}): ${error.message}`)
  return data
}

// ─── Access codes ─────────────────────────────────────────────────────────────

/**
 * Validate a venue access code. Returns the venue if the code is valid,
 * not expired and not revoked. Returns null otherwise. Never throws for a
 * bad code — throwing is reserved for infrastructure errors.
 */
export async function validateAccessCode(code: string): Promise<{
  venue: Venue
  access_code: VenueAccessCode
} | null> {
  const trimmed = code.trim().toUpperCase()
  if (!trimmed) return null

  const { data: accessCode, error } = await supabase
    .from('venue_access_codes')
    .select('*, venues(*)')
    .eq('code', trimmed)
    .is('revoked_at', null)
    .maybeSingle()

  if (error) throw new Error(`validateAccessCode: ${error.message}`)
  if (!accessCode) return null

  // Expiry check, if set
  if (accessCode.expires_at && new Date(accessCode.expires_at).getTime() < Date.now()) {
    return null
  }

  const venue = (accessCode as unknown as { venues: Venue }).venues
  if (!venue) return null

  return { venue, access_code: accessCode as VenueAccessCode }
}

// ─── Picker sessions ──────────────────────────────────────────────────────────

export async function createPickerSession(params: {
  venue_id:        string
  session_type:    'shared_code' | 'named_invite' | 'admin'
  access_code_id?: string
  invited_email?:  string
  display_name?:   string
}): Promise<VenuePicker> {
  const { data, error } = await supabase
    .from('venue_pickers')
    .insert({
      venue_id:       params.venue_id,
      session_type:   params.session_type,
      access_code_id: params.access_code_id,
      invited_email:  params.invited_email,
      display_name:   params.display_name ?? 'picker',
    })
    .select()
    .single()
  if (error) throw new Error(`createPickerSession: ${error.message}`)
  return data
}

/**
 * createDemoSession — bypasses code entry for known demo venues.
 * Looks up the venue by slug, creates a venue_pickers row, stores the
 * session in localStorage. Used by VenueProtectedLayout when no session
 * exists so the picker can navigate directly to any venue route.
 */
export async function createDemoSession(slug: string, displayName = 'Demo'): Promise<PickerSession | null> {
  try {
    const venue = await fetchVenueBySlug(slug)
    if (!venue) return null

    const { data, error } = await supabase
      .from('venue_pickers')
      .insert({ venue_id: venue.id, session_type: 'shared_code', display_name: displayName })
      .select()
      .single()
    if (error) return null

    const session: PickerSession = {
      picker_id:    data.id,
      venue_id:     venue.id,
      venue_slug:   slug,
      display_name: displayName,
      session_type: 'shared_code',
      started_at:   new Date().toISOString(),
    }
    setPickerSession(session)
    return session
  } catch {
    return null
  }
}

export async function touchPickerSession(pickerId: string): Promise<void> {
  const { error } = await supabase
    .from('venue_pickers')
    .update({ last_seen_at: new Date().toISOString() })
    .eq('id', pickerId)
  if (error) {
    // Non-fatal — last_seen is cosmetic
    console.warn('touchPickerSession failed:', error.message)
  }
}

// ─── Venue picks (the digest) ─────────────────────────────────────────────────

/**
 * Fetch the morning digest for a venue. Returns picks ordered by ai_score desc,
 * with their joined media items. `since` defaults to 24hr ago to match "last
 * night" but can be overridden.
 */
export async function fetchDigestPicks(params: {
  venue_id:     string
  since?:       string           // ISO timestamp, default 24hr ago
  limit?:       number           // default 50
  action_filter?: PickerAction | 'unactioned'
}): Promise<VenuePick[]> {
  const since  = params.since  ?? new Date(Date.now() - 24 * 3600 * 1000).toISOString()
  const limit  = params.limit  ?? 50

  let q = supabase
    .from('venue_picks')
    .select(`
      id, venue_id, media_item_id, ai_score, ai_reasoning,
      picker_action, actioned_by, actioned_at, created_at,
      media:media_items (
        id, cloudinary_url, cloudinary_id, media_type, file_name,
        ai_tags, ai_description, ai_mood, ai_route_score,
        status, created_at,
        approval_requirement, approval_reasoning,
        photographer_credit
      )
    `)
    .eq('venue_id', params.venue_id)
    .gte('created_at', since)
    .order('ai_score', { ascending: false, nullsFirst: false })
    .limit(limit)

  if (params.action_filter === 'unactioned') {
    q = q.is('picker_action', null)
  } else if (params.action_filter) {
    q = q.eq('picker_action', params.action_filter)
  }

  const { data, error } = await q
  if (error) throw new Error(`fetchDigestPicks: ${error.message}`)

  // Supabase returns joined arrays even for one-to-one; flatten.
  return (data ?? []).map(row => {
    const r = row as VenuePick & { media?: MediaItem | MediaItem[] | null }
    const media = Array.isArray(r.media) ? r.media[0] : r.media
    return { ...r, media: media ?? undefined }
  })
}

/**
 * Count stats for the "last night at a glance" header.
 */
export async function fetchDigestStats(params: {
  venue_id: string
  since?:   string
}): Promise<{
  total:           number
  unactioned:      number
  needs_approval:  number
  posted:          number
  rejected:        number
}> {
  const since = params.since ?? new Date(Date.now() - 24 * 3600 * 1000).toISOString()

  // Single query returning full set — counting client-side is fine for
  // demo volumes (<1000 picks). If this scales, move to SQL aggregate RPC.
  const { data, error } = await supabase
    .from('venue_picks')
    .select('id, picker_action')
    .eq('venue_id', params.venue_id)
    .gte('created_at', since)

  if (error) throw new Error(`fetchDigestStats: ${error.message}`)

  const all = data ?? []
  return {
    total:          all.length,
    unactioned:     all.filter(p => p.picker_action === null).length,
    needs_approval: all.filter(p => p.picker_action === 'needs_approval').length,
    posted:         all.filter(p => p.picker_action === 'posted').length,
    rejected:       all.filter(p => p.picker_action === 'rejected').length,
  }
}

export async function updatePickerAction(params: {
  pick_id:    string
  picker_id:  string
  action:     PickerAction
}): Promise<VenuePick> {
  const { data, error } = await supabase
    .from('venue_picks')
    .update({
      picker_action: params.action,
      actioned_by:   params.picker_id,
      actioned_at:   new Date().toISOString(),
    })
    .eq('id', params.pick_id)
    .select()
    .single()
  if (error) throw new Error(`updatePickerAction: ${error.message}`)
  return data
}

// ─── Approval decisions audit log ─────────────────────────────────────────────

export async function logApprovalDecision(params: {
  pick_id:          string
  picker_id?:       string
  decision:         'override_not_required' | 'requested' | 'expired' | 'waived' | 'approved' | 'declined'
  reason?:          string
  reasoning_notes?: string
}): Promise<void> {
  const { error } = await supabase
    .from('approval_decisions_log')
    .insert({
      venue_pick_id:   params.pick_id,
      decided_by:      params.picker_id,
      decision:        params.decision,
      reason:          params.reason,
      reasoning_notes: params.reasoning_notes,
    })
  if (error) throw new Error(`logApprovalDecision: ${error.message}`)
}

// ─── Approval requests ────────────────────────────────────────────────────────

/**
 * Generate a cryptographically-random access token for an approval request.
 * Uses Web Crypto (available in browsers and Deno). 32 bytes → 43-char
 * base64url, which is the format used by the /approve/:token route.
 */
function generateAccessToken(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

export async function createApprovalRequest(params: {
  pick_id:              string
  approver_name:        string
  approver_contact:     string
  approver_relationship: ApproverRelationship
  channel:              ApprovalChannel
  picker_id?:           string
  expires_in_hours?:    number  // default 72
}): Promise<ApprovalRequest> {
  const expiresIn = params.expires_in_hours ?? 72
  const expiresAt = new Date(Date.now() + expiresIn * 3600 * 1000).toISOString()
  const token     = generateAccessToken()

  const { data, error } = await supabase
    .from('approval_requests')
    .insert({
      venue_pick_id:         params.pick_id,
      approver_name:         params.approver_name,
      approver_contact:      params.approver_contact,
      approver_relationship: params.approver_relationship,
      channel:               params.channel,
      access_token:          token,
      expires_at:            expiresAt,
      created_by:            params.picker_id,
    })
    .select()
    .single()
  if (error) throw new Error(`createApprovalRequest: ${error.message}`)

  // Mark the pick as needs_approval and log the decision
  if (params.picker_id) {
    await updatePickerAction({
      pick_id:   params.pick_id,
      picker_id: params.picker_id,
      action:    'needs_approval',
    })
    await logApprovalDecision({
      pick_id:    params.pick_id,
      picker_id:  params.picker_id,
      decision:   'requested',
      reason:     `Sent to ${params.approver_name} via ${params.channel}`,
    })
  }

  return data
}

/**
 * Fetch an approval request by its token. Used by the public /approve/:token
 * route. Returns null for unknown token — the public page renders a friendly
 * "not found or expired" message in that case.
 */
export async function fetchApprovalRequestByToken(token: string): Promise<{
  request: ApprovalRequest
  pick:    VenuePick
  venue:   Venue
} | null> {
  const { data, error } = await supabase
    .from('approval_requests')
    .select(`
      *,
      pick:venue_picks (
        *,
        media:media_items (*),
        venue:venues (*)
      )
    `)
    .eq('access_token', token)
    .maybeSingle()

  if (error) throw new Error(`fetchApprovalRequestByToken: ${error.message}`)
  if (!data) return null

  // Expiry check
  if (new Date(data.expires_at).getTime() < Date.now()) {
    // Mark as expired if not already responded-to
    if (data.status === 'pending') {
      await supabase
        .from('approval_requests')
        .update({ status: 'expired' })
        .eq('id', data.id)
    }
    return null
  }

  const raw  = data as unknown as {
    pick: (VenuePick & { media: MediaItem | MediaItem[]; venue: Venue | Venue[] })
  }
  const pick = raw.pick
  if (!pick) return null

  const media = Array.isArray(pick.media) ? pick.media[0] : pick.media
  const venue = Array.isArray(pick.venue) ? pick.venue[0] : pick.venue

  if (!venue) return null

  return {
    request: data as ApprovalRequest,
    pick:    { ...pick, media: media ?? undefined },
    venue,
  }
}

export async function respondToApprovalRequest(params: {
  token:          string
  decision:       'approved' | 'declined' | 'changes_requested'
  response_notes?: string
}): Promise<ApprovalRequest> {
  const { data, error } = await supabase
    .from('approval_requests')
    .update({
      status:         params.decision,
      response_notes: params.response_notes,
      responded_at:   new Date().toISOString(),
    })
    .eq('access_token', params.token)
    .eq('status', 'pending')        // guard against double-response
    .select()
    .single()
  if (error) throw new Error(`respondToApprovalRequest: ${error.message}`)

  // Cascade to the pick — approved means clear_to_post, declined means back
  // to rejected so the picker sees the outcome.
  if (params.decision === 'approved') {
    await supabase
      .from('venue_picks')
      .update({ picker_action: null })  // re-queues for picker to post with confidence
      .eq('id', data.venue_pick_id)
    await supabase
      .from('approval_decisions_log')
      .insert({
        venue_pick_id: data.venue_pick_id,
        decision:      'approved',
        reason:        `Approver responded: approved${params.response_notes ? ' — ' + params.response_notes : ''}`,
      })
  } else if (params.decision === 'declined') {
    await supabase
      .from('venue_picks')
      .update({ picker_action: 'rejected' })
      .eq('id', data.venue_pick_id)
    await supabase
      .from('approval_decisions_log')
      .insert({
        venue_pick_id: data.venue_pick_id,
        decision:      'declined',
        reason:        `Approver responded: declined${params.response_notes ? ' — ' + params.response_notes : ''}`,
      })
  }

  return data
}

export async function fetchApprovalQueue(venueId: string): Promise<Array<{
  request: ApprovalRequest
  pick:    VenuePick
}>> {
  const { data, error } = await supabase
    .from('approval_requests')
    .select(`
      *,
      pick:venue_picks!inner (
        *,
        media:media_items (*)
      )
    `)
    .eq('pick.venue_id', venueId)
    .in('status', ['pending', 'changes_requested'])
    .order('created_at', { ascending: false })

  if (error) throw new Error(`fetchApprovalQueue: ${error.message}`)

  return (data ?? []).map(row => {
    const r    = row as unknown as ApprovalRequest & {
      pick: VenuePick & { media: MediaItem | MediaItem[] }
    }
    const pick = r.pick
    const media = Array.isArray(pick.media) ? pick.media[0] : pick.media
    return {
      request: row as ApprovalRequest,
      pick:    { ...pick, media: media ?? undefined },
    }
  })
}

// ─── Approval policies (future: training data) ────────────────────────────────

export async function fetchApprovalPolicies(venueId: string): Promise<ApprovalPolicy[]> {
  const { data, error } = await supabase
    .from('approval_policies')
    .select('*')
    .eq('venue_id', venueId)
  if (error) throw new Error(`fetchApprovalPolicies: ${error.message}`)
  return data ?? []
}
